import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { useSelector } from "react-redux";
import TeamTemplate from "work/team-templates/team-template";
import ActiveTeamDisplay from "_shared/teams/active-team-display";
import { MAX_VISIBLE_TEAM_MEMBERS } from "_shared/teams/team-members-display";

const MembersContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
}));

const TeamColumnLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  display: "block",
  textDecorationColor: theme.palette.common.black,
  cursor: "pointer",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const ViewAllLink = styled("button")(({ theme }) => ({
  alignSelf: "flex-start",
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
  whiteSpace: "nowrap",
}));

type MembersColumnCellProps = {
  teamTemplate?: TeamTemplate;
  teamUserIds?: Guid[];
  activeTeam?: ActiveTeamDisplay;
  showAllMembers?: boolean;
};


export default function MembersColumnCell(props: Readonly<MembersColumnCellProps>) {
  const { teamTemplate, teamUserIds, activeTeam } = props;
  const showAllMembers = props.showAllMembers ?? false;
  const { openDialog, closeDialog } = useDialog();

  let membersUserIds: Guid[] = [];

  if (activeTeam) {
    membersUserIds = activeTeam.members?.map((m) => m.userId) || [];
  } else if (teamTemplate) {
    membersUserIds = teamTemplate.members?.map((m) => m.userId) || [];
  } else if (teamUserIds) {
    membersUserIds = teamUserIds;
  }

  const teamIndividualsResponse = useSelector(getIndividualsByUserIds(membersUserIds));

  if (!teamIndividualsResponse || teamIndividualsResponse.length === 0) {
    return <Icon>remove</Icon>;
  }

  const visibleIndividuals = showAllMembers
    ? teamIndividualsResponse
    : teamIndividualsResponse.slice(0, MAX_VISIBLE_TEAM_MEMBERS);
  const hasHiddenMembers = visibleIndividuals.length < teamIndividualsResponse.length;

  return (
    <MembersContainer>
      {visibleIndividuals.map((individual: Individual) => (
        <TeamColumnLink
          key={individual.userId.value}
          title={individual.profile.fullName}
          onClick={(event) => {
            event.stopPropagation();
            openDialog({
              component: <ViewIndividualProfile profile={individual.profile} individualId={individual.id} />,
              contentSxProps: {
                display: "flex",
              },
              MuiProps: {
                fullWidth: true,
                maxWidth: "lg",
              },
              titleStyle: {
                paddingBottom: 0
              },
              onClose: closeDialog,
            });
          }}
        >
          {individual.profile.fullName}
        </TeamColumnLink>
      ))}
      {hasHiddenMembers && (
        <ViewAllLink
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openDialog({
              title: `Team members (${teamIndividualsResponse.length})`,
              component: (
                <MembersColumnCell
                  teamTemplate={teamTemplate}
                  teamUserIds={teamUserIds}
                  activeTeam={activeTeam}
                  showAllMembers
                />
              ),
              MuiProps: {
                fullWidth: true,
                maxWidth: "sm",
              },
              onClose: closeDialog,
            });
          }}
        >
          View all ({teamIndividualsResponse.length})
        </ViewAllLink>
      )}
    </MembersContainer>
  );
}
